import { SENSITIVE_LOCAL_KEYS, encryptData, decryptData } from "./crypto";
import { SYNC_KEYS, syncKeyToD1, getAuthHeaders } from "./d1-sync";
import { PracticeSession } from "../types";
import { api } from "./api";

// Transparent Synchronous cache for decrypted sensitive keys
const decryptedCache: Record<string, string> = {};

let isStorageProxyActive = false;

// Direct bound reference helpers for localStorage using Storage prototype
const storageInstance = typeof window !== "undefined" ? window.localStorage : null;
const nativeGetItem = typeof Storage !== "undefined" ? Storage.prototype.getItem : null;
const nativeSetItem = typeof Storage !== "undefined" ? Storage.prototype.setItem : null;
const nativeRemoveItem = typeof Storage !== "undefined" ? Storage.prototype.removeItem : null;

function rawGetItem(key: string): string | null {
  if (storageInstance && nativeGetItem) {
    try {
      return nativeGetItem.call(storageInstance, key);
    } catch (e) {
      // ignore
    }
  }
  if (storageInstance) {
    try {
      const val = (storageInstance as any)[key];
      if (typeof val === "string") return val;
      if (val === null || val === undefined) return null;
      return String(val);
    } catch (e) {
      // ignore
    }
  }
  return null;
}

function rawSetItem(key: string, value: string): void {
  if (storageInstance && nativeSetItem) {
    try {
      nativeSetItem.call(storageInstance, key, value);
      return;
    } catch (e) {
      // ignore
    }
  }
  if (storageInstance) {
    try {
      (storageInstance as any)[key] = value;
    } catch (e) {
      // ignore
    }
  }
}

function rawRemoveItem(key: string): void {
  if (storageInstance && nativeRemoveItem) {
    try {
      nativeRemoveItem.call(storageInstance, key);
      return;
    } catch (e) {
      // ignore
    }
  }
  if (storageInstance) {
    try {
      delete (storageInstance as any)[key];
    } catch (e) {
      // ignore
    }
  }
}

// Safely dispatch events without causing uncaught errors
const safeDispatchVedicEvent = (name: string, detail?: any) => {
  try {
    if (typeof window !== "undefined" && typeof (window as any).dispatchVedicEvent === "function") {
      (window as any).dispatchVedicEvent(name, detail);
    }
  } catch (err) {
    // Silent catch
  }
};

// Pre-load and decrypt sensitive keys in the background during app bootstrap
const bootstrapDecryption = async () => {
  if (!storageInstance) return;

  for (const key of SENSITIVE_LOCAL_KEYS) {
    try {
      const rawValue = rawGetItem(key);
      if (rawValue) {
        if (rawValue.startsWith("__enc__:")) {
          try {
            const ciphertext = rawValue.substring(8);
            const plaintext = await decryptData(ciphertext);
            decryptedCache[key] = plaintext;
          } catch (err) {
            console.warn(`[StorageProxy] Bootstrap decryption failed for ${key}`, err);
          }
        } else {
          // Plaintext fallback
          decryptedCache[key] = rawValue;
        }
      }
    } catch (err) {
      // Prevent crash
    }
  }

  safeDispatchVedicEvent("storage");
  safeDispatchVedicEvent("vedic-history-updated");
  safeDispatchVedicEvent("vedic-settings-updated");
  safeDispatchVedicEvent("vedic-steps-updated");
};

// Intercept localStorage operations on window.localStorage safely
if (storageInstance) {
  try {
    const customGetItem = function (key: string): string | null {
      const isBypassed = (window as any).__bypass_storage_proxy__ === true;
      if (isBypassed) {
        return rawGetItem(key);
      }

      try {
        if (SENSITIVE_LOCAL_KEYS.includes(key)) {
          if (key in decryptedCache) {
            return decryptedCache[key];
          }

          const rawValue = rawGetItem(key);
          if (!rawValue) return null;

          if (rawValue.startsWith("__enc__:")) {
            const ciphertext = rawValue.substring(8);
            decryptData(ciphertext).then(plaintext => {
              decryptedCache[key] = plaintext;
              safeDispatchVedicEvent("storage");
              safeDispatchVedicEvent("vedic-history-updated");
              safeDispatchVedicEvent("vedic-settings-updated");
              safeDispatchVedicEvent("vedic-steps-updated");
            }).catch(() => {});

            if (key === "vedic_history") return "[]";
            return null;
          }

          return rawValue;
        }

        return rawGetItem(key);
      } catch (e) {
        console.warn("[StorageProxy] Error in proxied getItem:", e);
        return rawGetItem(key);
      }
    };

    const customSetItem = function (key: string, value: string): void {
      const isBypassed = (window as any).__bypass_storage_proxy__ === true;
      if (isBypassed) {
        rawSetItem(key, value);
        return;
      }

      try {
        let oldValue: string | null = null;
        try {
          oldValue = SENSITIVE_LOCAL_KEYS.includes(key)
            ? decryptedCache[key] || null
            : rawGetItem(key);
        } catch (e) {
          oldValue = rawGetItem(key);
        }

        if (SENSITIVE_LOCAL_KEYS.includes(key)) {
          decryptedCache[key] = value;

          encryptData(value).then(ciphertext => {
            try {
              rawSetItem(key, `__enc__:${ciphertext}`);
            } catch (e) {
              rawSetItem(key, value);
            }
          }).catch(err => {
            console.error(`[StorageProxy] Encryption failed for ${key}, falling back to plaintext`, err);
            rawSetItem(key, value);
          });
        } else {
          rawSetItem(key, value);
        }

        if (key === "vedic_history") {
          try {
            const oldList: PracticeSession[] = oldValue ? JSON.parse(oldValue) : [];
            const newList: PracticeSession[] = value ? JSON.parse(value) : [];

            if (newList.length === 0) {
              api.api.history.$delete({}, { headers: getAuthHeaders() }).catch(() => {});
            } else {
              for (const newSession of newList) {
                const oldSession = oldList.find(s => s.id === newSession.id);
                if (!oldSession || JSON.stringify(oldSession) !== JSON.stringify(newSession)) {
                  encryptData(newSession.notes || "").then(encryptedNotes => {
                    const encryptedSession = { ...newSession, notes: encryptedNotes };
                    api.api.history.$post(
                      { json: encryptedSession },
                      { headers: getAuthHeaders() }
                    ).catch(() => {});
                  });
                }
              }
              for (const oldSession of oldList) {
                const stillExists = newList.some(s => s.id === oldSession.id);
                if (!stillExists) {
                  api.api.history.$delete(
                    { query: { id: oldSession.id } },
                    { headers: getAuthHeaders() }
                  ).catch(() => {});
                }
              }
            }
          } catch (err) {
            console.warn("[StorageProxy] History sync error:", err);
          }
        } else if (SYNC_KEYS.includes(key)) {
          syncKeyToD1(key, value);
        }
      } catch (e) {
        console.warn("[StorageProxy] Error in proxied setItem:", e);
        rawSetItem(key, value);
      }
    };

    const customRemoveItem = function (key: string): void {
      const isBypassed = (window as any).__bypass_storage_proxy__ === true;
      if (isBypassed) {
        rawRemoveItem(key);
        return;
      }

      try {
        if (SENSITIVE_LOCAL_KEYS.includes(key)) {
          delete decryptedCache[key];
        }

        rawRemoveItem(key);

        if (key === "vedic_history") {
          api.api.history.$delete({}, { headers: getAuthHeaders() }).catch(() => {});
        } else if (SYNC_KEYS.includes(key)) {
          syncKeyToD1(key, "");
        }
      } catch (e) {
        console.warn("[StorageProxy] Error in proxied removeItem:", e);
        rawRemoveItem(key);
      }
    };

    try {
      if (typeof Storage !== "undefined" && Storage.prototype) {
        Storage.prototype.getItem = function (this: Storage, key: string): string | null {
          if (this === storageInstance) {
            return customGetItem(key);
          }
          return nativeGetItem ? nativeGetItem.call(this, key) : null;
        };

        Storage.prototype.setItem = function (this: Storage, key: string, value: string): void {
          if (this === storageInstance) {
            customSetItem(key, value);
            return;
          }
          if (nativeSetItem) nativeSetItem.call(this, key, value);
        };

        Storage.prototype.removeItem = function (this: Storage, key: string): void {
          if (this === storageInstance) {
            customRemoveItem(key);
            return;
          }
          if (nativeRemoveItem) nativeRemoveItem.call(this, key);
        };

        isStorageProxyActive = true;
      }
    } catch (e) {
      console.warn("[StorageProxy] Failed to override Storage.prototype:", e);
    }
  } catch (err) {
    console.warn("[StorageProxy] Failed to apply overrides to localStorage instance:", err);
  }
}

// Start bootstrap decryption safely
if (isStorageProxyActive) {
  bootstrapDecryption().catch(err => {
    console.error("[StorageProxy] Bootstrap decryption error:", err);
  });
}
